import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle, 
  AlertCircle, 
  Eye, 
  Edit3, 
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Star
} from 'lucide-react';
import { RevisionSession, ChangeItem, ChangeItemOption } from '@/types/resumeRevision';
import { ResumeRevisionService } from '@/services/resumeRevisionService';
import { useToast } from '@/components/ui/use-toast';

interface Step3ChangeItemsProps {
  session: RevisionSession;
  onSessionUpdate: (session: RevisionSession) => void;
}

interface ChangeItemCardProps {
  changeItem: ChangeItem;
  onOptionSelect: (optionId: string) => void;
  onUserTweak: (text: string) => void;
  onApprove: () => void;
}

const ChangeItemCard: React.FC<ChangeItemCardProps> = ({
  changeItem,
  onOptionSelect,
  onUserTweak,
  onApprove
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [userTweakText, setUserTweakText] = useState(changeItem.user_tweak_text || '');
  const [previewMode, setPreviewMode] = useState<'normal' | 'highlight' | 'track'>('normal');

  const selectedOption = changeItem.options?.find(opt => opt.selected_bool);
  const topOption = changeItem.options?.reduce((prev, current) => 
    (prev.score > current.score) ? prev : current
  );

  return (
    <Card className="border-l-4 border-l-blue-500">
      <CardHeader className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{changeItem.instruction_text}</CardTitle>
              {changeItem.status === 'completed' && (
                <CheckCircle className="h-4 w-4 text-green-500" />
              )}
              {changeItem.status === 'pending' && (
                <AlertCircle className="h-4 w-4 text-yellow-500" />
              )}
            </div>
            
            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Original:</span> "{changeItem.original_text.slice(0, 100)}..."
            </div>
            
            {selectedOption && (
              <div className="text-sm">
                <span className="font-medium text-green-600">Selected:</span> "{selectedOption.text.slice(0, 100)}..."
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {changeItem.options && (
              <Badge variant="outline">
                {changeItem.options.length} options
              </Badge>
            )}
            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="space-y-4">
          {/* Options */}
          {changeItem.options && changeItem.options.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-medium">AI Generated Options</h4>
              
              {changeItem.options.map((option, index) => (
                <div
                  key={option.id}
                  className={`
                    p-4 rounded-lg border cursor-pointer transition-colors
                    ${option.selected_bool ? 'border-green-500 bg-green-50' : 'border-border hover:border-primary/50'}
                    ${option.id === topOption?.id ? 'ring-2 ring-blue-200' : ''}
                  `}
                  onClick={() => onOptionSelect(option.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant={option.selected_bool ? 'default' : 'secondary'}>
                          Option {index + 1}
                        </Badge>
                        
                        {option.id === topOption?.id && (
                          <Badge variant="outline" className="text-blue-600 border-blue-600">
                            <Star className="h-3 w-3 mr-1" />
                            Recommended
                          </Badge>
                        )}
                        
                        <Badge variant="outline" className="text-xs">
                          Score: {option.score}
                        </Badge>
                      </div>
                      
                      <div className="text-sm">
                        <p className="font-medium">Before:</p>
                        <p className="text-muted-foreground italic">"{changeItem.original_text}"</p>
                      </div>
                      
                      <div className="text-sm">
                        <p className="font-medium">After:</p>
                        <p className="font-medium">"{option.text}"</p>
                      </div>
                      
                      {option.reasons && option.reasons.length > 0 && (
                        <div className="text-xs">
                          <p className="font-medium">Why this fits:</p>
                          <p className="text-muted-foreground">{option.reasons.join(', ')}</p>
                        </div>
                      )}
                      
                      {/* Quality indicators */}
                      <div className="flex items-center gap-3 text-xs">
                        <div className={`flex items-center gap-1 ${option.style_ok ? 'text-green-600' : 'text-red-600'}`}>
                          <div className={`h-2 w-2 rounded-full ${option.style_ok ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          Style
                        </div>
                        <div className={`flex items-center gap-1 ${option.ats_ok ? 'text-green-600' : 'text-red-600'}`}>
                          <div className={`h-2 w-2 rounded-full ${option.ats_ok ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          ATS
                        </div>
                        <div className={`flex items-center gap-1 ${option.length_ok ? 'text-green-600' : 'text-red-600'}`}>
                          <div className={`h-2 w-2 rounded-full ${option.length_ok ? 'bg-green-500' : 'bg-red-500'}`}></div>
                          Length
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* User Tweaks */}
          {selectedOption && (
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Edit3 className="h-4 w-4" />
                Fine-tune Selection
              </h4>
              
              <Textarea
                placeholder="Make small adjustments to the selected option..."
                value={userTweakText}
                onChange={(e) => setUserTweakText(e.target.value)}
                className="min-h-20"
              />
              
              <Button 
                size="sm" 
                variant="outline"
                onClick={() => onUserTweak(userTweakText)}
              >
                Apply Tweaks
              </Button>
            </div>
          )}

          {/* Preview Controls */}
          <div className="space-y-3">
            <h4 className="font-medium flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Preview Options
            </h4>
            
            <Tabs value={previewMode} onValueChange={(value) => setPreviewMode(value as any)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="normal">Normal</TabsTrigger>
                <TabsTrigger value="highlight">Highlight</TabsTrigger>
                <TabsTrigger value="track">Track Changes</TabsTrigger>
              </TabsList>
              
              <TabsContent value="normal" className="mt-3">
                <div className="p-3 bg-slate-50 rounded border text-sm">
                  {selectedOption ? selectedOption.text : changeItem.original_text}
                </div>
              </TabsContent>
              
              <TabsContent value="highlight" className="mt-3">
                <div className="p-3 bg-slate-50 rounded border text-sm">
                  <span className="bg-yellow-200 px-1 rounded">
                    {selectedOption ? selectedOption.text : changeItem.original_text}
                  </span>
                </div>
              </TabsContent>
              
              <TabsContent value="track" className="mt-3">
                <div className="p-3 bg-slate-50 rounded border text-sm space-y-1">
                  <div className="line-through text-red-600 decoration-red-500">
                    {changeItem.original_text}
                  </div>
                  {selectedOption && (
                    <div className="text-green-600">
                      {selectedOption.text}
                    </div>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm">
              <MessageSquare className="h-4 w-4 mr-1" />
              Ask AI
            </Button>
            <Button 
              size="sm"
              onClick={onApprove}
              disabled={!selectedOption}
            >
              Apply Changes
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export const Step3ChangeItems: React.FC<Step3ChangeItemsProps> = ({
  session,
  onSessionUpdate
}) => {
  const { toast } = useToast();
  const [changeItems, setChangeItems] = useState<ChangeItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (session.change_items && session.change_items.length > 0) {
      setChangeItems(session.change_items);
    } else {
      // Load change items from the service
      loadChangeItems();
    }
  }, [session.id]);

  const loadChangeItems = async () => {
    setIsProcessing(true);
    try {
      const items = await ResumeRevisionService.getChangeItems(session.id);
      setChangeItems(items);
    } catch (error) {
      console.error('Error loading change items:', error);
      toast({
        title: "Error",
        description: "Failed to load change items.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOptionSelect = async (changeItemId: string, optionId: string) => {
    try {
      await ResumeRevisionService.selectOption(changeItemId, optionId);
      
      setChangeItems(prev => prev.map(item => {
        if (item.id === changeItemId) {
          return {
            ...item,
            chosen_option_id: optionId,
            options: item.options?.map(opt => ({
              ...opt,
              selected_bool: opt.id === optionId
            }))
          };
        }
        return item;
      }));
    } catch (error) {
      console.error('Error selecting option:', error);
      toast({
        title: "Error",
        description: "Failed to select option.",
        variant: "destructive"
      });
    }
  };

  const handleUserTweak = async (changeItemId: string, text: string) => {
    try {
      await ResumeRevisionService.addUserTweak(changeItemId, text);
      
      setChangeItems(prev => prev.map(item => {
        if (item.id === changeItemId) {
          return { ...item, user_tweak_text: text };
        }
        return item;
      }));
    } catch (error) {
      console.error('Error adding user tweak:', error);
      toast({
        title: "Error", 
        description: "Failed to save user tweaks.",
        variant: "destructive"
      });
    }
  };

  const handleApprove = async (changeItemId: string) => {
    try {
      await ResumeRevisionService.applyChange(changeItemId);
      
      setChangeItems(prev => prev.map(item => {
        if (item.id === changeItemId) {
          return { ...item, status: 'completed' as const };
        }
        return item;
      }));

      toast({
        title: "Change Applied",
        description: "The selected revision has been applied to your resume.",
      });
    } catch (error) {
      console.error('Error applying change:', error);
      toast({
        title: "Error",
        description: "Failed to apply change.",
        variant: "destructive"
      });
    }
  };

  if (isProcessing) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-center items-center h-32">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-sm text-muted-foreground">
                Analyzing your revision notes and generating change options...
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const completedItems = changeItems.filter(item => item.status === 'completed').length;
  const totalItems = changeItems.length;

  return (
    <div className="space-y-6">
      {/* Progress Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Review & Apply Changes</span>
            <Badge variant="outline">
              {completedItems}/{totalItems} Complete
            </Badge>
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Review AI-generated options for each requested change. Select your preferred option and apply tweaks if needed.
          </p>
        </CardHeader>
      </Card>

      {/* Change Items */}
      <div className="space-y-4">
        {changeItems.map((item) => (
          <ChangeItemCard
            key={item.id}
            changeItem={item}
            onOptionSelect={(optionId) => handleOptionSelect(item.id, optionId)}
            onUserTweak={(text) => handleUserTweak(item.id, text)}
            onApprove={() => handleApprove(item.id)}
          />
        ))}
      </div>

      {changeItems.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Changes Generated</h3>
              <p className="text-muted-foreground mb-4">
                No specific revision tasks could be extracted from your notes. Try providing more detailed instructions.
              </p>
              <Button variant="outline" onClick={loadChangeItems}>
                Regenerate Changes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};